import React, { useState, useEffect, useRef, lazy } from "react";
import { ColumnTypes, ColumnTypeIcons, ColumnTypeLabels } from "components/collections/util/datatypes";
import HelpMessage from "components/util/helpers/HelpMessage";
import { useAuth } from "components/util/helpers/providers/AuthProvider";
import l from "components/util/l";

const HeaderCellContextMenu = lazy(() => import("components/collections/table/HeaderCell.lib.contextMenu"));

const HeaderCell = ({
  style = {},
  column,
  filters,
  sorting,
  showFilters = true,
  showSorting = true,
  isReadOnly = false,
  isActive = false,
  modifyStructure = false,
  isDisableMoveLeft = false,
  isDisableMoveRight = false,
  setIsFilterModalShown,
  setIsSortingModalShown,
  setFilterDefault,
  setZipFieldName,
  setIsZipModalShown,
  onClickChangeColumn = () => { },
  onClickMoveLeftColumn = () => { },
  onClickMoveRightColumn = () => { },
  onClickDeleteColumn = () => { },
  onClickOpenColumnEditModal = () => { },
}) => {

  const cellRef = useRef(null);
  const user = useAuth();
  const isDeveloper = user?.isDeveloper;
  
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const inputRef = useRef(null);
  
  const [isEditing, setIsEditing] = useState(false);
  const [isContextMenuShown, setIsContextMenuShown] = useState(false);

  const componentId = useRef(Math.random().toString(36).substring(7));
  const isFilterActive = column.name && JSON.stringify(filters).includes(column.name);
  const isSortingActive = column.name && JSON.stringify(sorting).includes(column.name);

  useEffect(() => {

    document.addEventListener("click", (e) => {
      if (cellRef?.current && !cellRef?.current?.contains(e.target) &&
        e.target?.getAttribute("ignore-onclickoutside") != componentId.current) {
        setIsEditing(false);
      }
    });

    // every right click will close the context menu
    document.addEventListener("mousedown", (e) => {
      if (cellRef?.current &&
        !cellRef?.current?.contains(e.target) &&
        e.target?.getAttribute("ignore-onclickoutside") != componentId.current
      ) {
        setIsContextMenuShown(false);
      }
    });

  }, []);

  useEffect(() => {
    inputRef?.current?.focus();
  }, [isEditing]);

  useEffect(() => {
    setTimeout(() => {
      if (isActive && !isReadOnly) 
        setIsEditing(true);
    }, 15);
  }, [isActive, isReadOnly]);

  const onClick = (e) => {
    if (isReadOnly) return;
    setIsEditing(true);
    e.stopPropagation();
  }

  const onFilter = () => {
    setIsFilterModalShown(true);
    setIsContextMenuShown(false);
    setFilterDefault(column.name);
  }

  const onSorting = () => {
    setIsSortingModalShown(true);
    setIsContextMenuShown(false);
  }

  const onContextMenu = (e) => {

    e.preventDefault();
    e.stopPropagation();
    setIsContextMenuShown(!isContextMenuShown);

    const x = e.clientX - cellRef.current.getBoundingClientRect().x;
    const y = e.clientY - cellRef.current.getBoundingClientRect().y;
    setCoords({ x, y });

  }

  const onChangeColumnLabel = (e) => {
    setIsEditing(false);
    onClickChangeColumn({
      ...column,
      label: e.target.value,
    });
  }

  if (column.visible === false)
    return null;

  return (
    <>

      <th
        ref={cellRef}
        className="table-column m-0 p-0 h5 font-weight-bold"
        onClick={onClick}
        onContextMenu={onContextMenu}
        style={style}
      >

        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            className="form-control form-control-sm d-flex flex-wrap py-0 m-0 w-100 h-100"
            style={{ minWidth: "120px" }}
            defaultValue={column.label}
            onKeyUp={(e) => {
              if (e.key === "Enter")
                onChangeColumnLabel(e);
            }}
            onBlur={onChangeColumnLabel}
          />
        ) : (
          <div className="table-column-content d-flex align-items-center px-2 py-1 m-0 w-100 h-100 text-truncate">

            <div className="d-flex align-items-center w-100 h-100">

              <small className={`column-type d-flex align-items-center`}>
                <HelpMessage
                  message={l("Tipo de campo%CA088") + ": " + ColumnTypeLabels[column.type] +
                    (column.type == ColumnTypes.COLLECTION ? " " + column.structure?.type : "")}
                  forceShow={true}
                >
                  <i className={ColumnTypeIcons[column.type]} />
                </HelpMessage>
              </small>

              <div className={`column-name text-truncate ${isFilterActive ? "font-italic" : ""}`}>
                <span>{column?.label}</span>
                {
                  isDeveloper &&
                  <small className="ml-1" onClick={e => e.stopPropagation()}><br />{column.name}</small>
                }
              </div>

              {
                isSortingActive &&
                <i
                  className="fas fa-sort mx-1"
                  style={{ fontSize: "0.8rem", cursor: "pointer" }}
                  onClick={onSorting}
                />
              }

              {
                isFilterActive &&
                <i
                  className="fas fa-filter mx-1"
                  style={{ fontSize: "0.8rem", cursor: "pointer" }}
                  onClick={onFilter}
                />
              }

              {
                column.type == ColumnTypes.COLLECTION && !column.structure && <>
                  <i className="fas fa-exclamation-circle mx-1 text-danger"></i>
                  <small> {l("No se ha seleccionado una tabla%CA089")} </small>
                </>
              }

              {
                column.type == ColumnTypes.ENUM && !column.structure && <>
                  <i className="fas fa-exclamation-circle mx-1 text-danger"></i>
                  <small> {l("No se han definido opciones%CA090")} </small>
                </>
              }

            </div>

            <div>
              <i
                className="column-options-icon fas fa-chevron-down ml-1 px-2"
                ignore-onclickoutside={componentId.current}
                onClick={onContextMenu}
              />
            </div>

          </div>
        )}

        {
          isContextMenuShown &&
          <HeaderCellContextMenu
            coords={coords}
            column={column}
            showFilters={showFilters}
            showSorting={showSorting}
            isReadOnly={isReadOnly}
            isDisableMoveLeft={isDisableMoveLeft}
            isDisableMoveRight={isDisableMoveRight}
            componentId={componentId}
            setIsContextMenuShown={setIsContextMenuShown}
            setIsZipModalShown={setIsZipModalShown}
            setZipFieldName={setZipFieldName}
            modifyStructure={modifyStructure}
            onClickMoveLeftColumn={onClickMoveLeftColumn}
            onClickMoveRightColumn={onClickMoveRightColumn}
            onClickDeleteColumn={onClickDeleteColumn}
            onClickOpenColumnEditModal={onClickOpenColumnEditModal}
            onFilter={onFilter}
            onSorting={onSorting}
          />
        }

      </th>

    </>
  );
}

export default HeaderCell;